import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Spin, Alert, Button, Segmented, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import {
  IconCoins,
  IconCalendarStats,
  IconChartBar,
  IconEngine,
  IconTool,
  IconTruck,
  IconShieldCheck,
  IconUsers,
  IconBriefcase,
  IconClock,
  IconReceipt,
} from '@tabler/icons-react';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import PageHeaderRow from '../../components/Layouts/PageHeaderRow';
import SegmentedPillToggle from '../../components/SegmentedPillToggle';
import StatTile from '../../components/StatTile';
import MiniTrendChart, { IMiniTrendPoint, MiniTrendVariant } from '../../components/MiniTrendChart';
import DateRangeFields from '../../components/DateRangeFields';
import HostEarningsWhereCard from '../../components/HostEarningsWhereCard';
import EarningsBreakdownCard, { IEarningsBreakdownRow } from '../../components/EarningsBreakdownCard';
import { getMyHostEarnings } from '../../services/HostEarningsService';
import { IHostEarnings, EarningsConcept, EarningsScope, EarningsPricingMode } from '../../interfaces/heavyDutyMachinery/IHostEarnings';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { formatCurrency } from '../../helpers/currencyHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { getLocalStorageKey, setLocalStorageKey } from '../../helpers/localStorageHelper';

const { Text, Title } = Typography;

/** 'custom' is not a shortcut pill -- it is what the date fields put the screen into. */
type RangeKey = 'month' | 'year' | 'trailing12' | 'custom';

/** Past this span a daily chart is a picket fence; the server coarsens at the same figure. */
const MAX_DAILY_SPAN_DAYS = 92;

const CHART_VARIANT_STORAGE_KEY = 'heavyDutyMachinery.hostEarnings.chartVariant';

/**
 * The concepts the server reports, in the order a host thinks about them:
 * the machine first, then what was added on top of it, then the people.
 * Keyed by the platform's own concept codes rather than by position, so a
 * concept the server stops sending simply does not draw a row.
 */
const CONCEPT_ORDER: EarningsConcept[] = ['Base', 'Accessories', 'Delivery', 'Insurance', 'Operator', 'Service'];

const CONCEPT_ICON: Record<EarningsConcept, React.ElementType> = {
  Base: IconEngine,
  Accessories: IconTool,
  Delivery: IconTruck,
  Insurance: IconShieldCheck,
  Operator: IconUsers,
  Service: IconBriefcase,
};

const CONCEPT_LABEL_KEY: Record<EarningsConcept, string> = {
  Base: TRANSLATIONS.HOST_EARNINGS_CONCEPT_BASE,
  Accessories: TRANSLATIONS.HOST_EARNINGS_CONCEPT_ACCESSORIES,
  Delivery: TRANSLATIONS.HOST_EARNINGS_CONCEPT_DELIVERY,
  Insurance: TRANSLATIONS.HOST_EARNINGS_CONCEPT_INSURANCE,
  Operator: TRANSLATIONS.HOST_EARNINGS_CONCEPT_OPERATOR,
  Service: TRANSLATIONS.HOST_EARNINGS_CONCEPT_SERVICE,
};

/**
 * The two ways a service can be billed. Only the two a booking can actually
 * be stamped with -- "Both" is a property of a listing, never of a sale (see
 * the backend's ServicePricingModes).
 */
const PRICING_MODE_LABEL_KEY: Record<EarningsPricingMode, string> = {
  Hourly: TRANSLATIONS.HOST_EARNINGS_PRICING_MODE_HOURLY,
  FixedJob: TRANSLATIONS.HOST_EARNINGS_PRICING_MODE_FIXED_JOB,
};

const PRICING_MODE_ICON: Record<EarningsPricingMode, React.ElementType> = {
  Hourly: IconClock,
  FixedJob: IconReceipt,
};

/**
 * How far the date fields let a host wander. The server refuses a window
 * wider than 1.100 days, and two years back to one year ahead is 1.095 --
 * so the picker cannot produce a range the server will reject.
 */
const EARLIEST_PICKABLE = () => dayjs().subtract(2, 'year').startOf('day');
const LATEST_PICKABLE = () => dayjs().add(1, 'year').endOf('day');

/**
 * The window each shortcut means, as whole calendar periods.
 *
 * "This month" is the WHOLE month, not the 1st to today. It used to stop at
 * today, which quietly contradicted the "Este mes" figure above it -- that one
 * has always been the calendar month, so a rental starting later this month
 * counted there and not in the panel, and the same screen showed two different
 * numbers under the same word. Same fix for the year.
 *
 * Rentals count by their start date (see the footnote), so a month does
 * legitimately include rentals that have not started yet but are already paid
 * for. That is money a host has earned; it belongs in the month it belongs to.
 */
const shortcutRange = (range: Exclude<RangeKey, 'custom'>): [Dayjs, Dayjs] => {
  // Anchored on today's UTC calendar date, not the browser's. The server's
  // own "hoy"/"este mes"/"este año" figures close in UTC, so a shortcut built
  // in local time would hand back a different month for anyone far enough
  // from UTC -- which is exactly the mismatch this pair of numbers already
  // had once. Only the calendar DATE crosses the wire (YYYY-MM-DD), so the
  // Dayjs objects below never carry a time zone anywhere that matters.
  const utcNow = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  const firstOfThisMonth = dayjs(`${utcNow.getUTCFullYear()}-${pad(utcNow.getUTCMonth() + 1)}-01`);

  if (range === 'year') return [firstOfThisMonth.startOf('year'), firstOfThisMonth.endOf('year')];
  if (range === 'trailing12') return [firstOfThisMonth.subtract(11, 'month'), firstOfThisMonth.endOf('month')];
  return [firstOfThisMonth, firstOfThisMonth.endOf('month')];
};

/** The query the server takes. Granularity is a request, not a promise -- see the response's own. */
const rangeQuery = ([from, to]: [Dayjs, Dayjs]) => ({
  from: from.format('YYYY-MM-DD'),
  to: to.format('YYYY-MM-DD'),
  granularity: (to.diff(from, 'day') <= MAX_DAILY_SPAN_DAYS ? 'day' : 'month') as 'day' | 'month',
});

/**
 * A host's own money, which until now only an admin could see: the same
 * figures AdminFinanceController has always had, on the side of the mirror
 * the person earning them is standing on.
 *
 * One screen, three questions, in the order a host asks them:
 *   1. How much have I made -- today, this month, this year.
 *   2. What does the trend look like, and which machine is carrying it.
 *   3. Where is the money right now.
 *
 * Every amount is NET (after the platform's commission), because that is the
 * number a host decides with. The commission is shown on its own line for
 * the selected range rather than quietly subtracted.
 *
 * Mobile and desktop are the same screen at two widths -- one column and a
 * scrollable chart on a phone, a two-column grid on a monitor. There is no
 * desktop-only or mobile-only content: everything here is worth having on
 * both, and the last thing this app needs is a fourth layout to keep in
 * step.
 */
const HostEarnings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();

  // Which shape the trend is drawn in, remembered per browser: it is a
  // reading preference, not data, and a host who prefers one should not have
  // to pick it again every visit. Reading it can throw in a browser with site
  // data blocked, and the answer there is simply the default.
  const [chartVariant, setChartVariant] = useState<MiniTrendVariant>(() => {
    try {
      return getLocalStorageKey(CHART_VARIANT_STORAGE_KEY) === 'line' ? 'line' : 'bars';
    } catch {
      return 'bars';
    }
  });

  const chooseChartVariant = useCallback((next: MiniTrendVariant) => {
    setChartVariant(next);
    try {
      setLocalStorageKey(CHART_VARIANT_STORAGE_KEY, next);
    } catch {
      // A preference that cannot be stored is still a preference for this
      // visit -- never let it take the screen down with it.
    }
  }, []);

  const [range, setRange] = useState<RangeKey>('month');
  // Which half of the business the range figures cover. Never persisted: it
  // is a question a host asks about this visit, not a preference, and coming
  // back to a screen that silently hides half your money would be a bug you
  // cannot see.
  const [scope, setScope] = useState<EarningsScope>('all');
  // Only set once somebody touches the date fields. Until then the shortcut
  // decides the window, and the fields simply show what it resolved to -- so
  // the control always reads as the window actually on screen rather than as
  // an empty pair of inputs next to a chart of something else.
  const [customWindow, setCustomWindow] = useState<[Dayjs, Dayjs]>();
  const [earnings, setEarnings] = useState<IHostEarnings>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const activeWindow = useMemo<[Dayjs, Dayjs]>(
    () => (range === 'custom' && customWindow ? customWindow : shortcutRange(range === 'custom' ? 'month' : range)),
    [range, customWindow]
  );

  // Keyed on the formatted query rather than on the Dayjs pair: two different
  // Dayjs objects for the same day are not equal, so depending on the pair
  // itself would refetch on every render.
  const query = useMemo(() => ({ ...rangeQuery(activeWindow), scope }), [activeWindow, scope]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      setEarnings(await getMyHostEarnings(query));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const money = useCallback(
    (amount: number) => formatCurrency(amount, earnings?.currencyCode ?? 'EUR', i18n.language),
    [earnings?.currencyCode, i18n.language]
  );

  const chartPoints: IMiniTrendPoint[] = useMemo(() => {
    if (!earnings) return [];
    const daily = earnings.granularity === 'day';
    const todayKey = new Date().toISOString().slice(0, daily ? 10 : 7);
    // A full month of daily bars is 30 labels at 11px, which is a grey smear.
    // Label every fifth day plus the ends, and let the tooltip carry the rest
    // -- the bars are the shape, the labels only need to anchor it.
    const thinLabels = daily && earnings.series.length > 12;
    return earnings.series.map((point, index) => {
      const date = new Date(point.periodStartUtc);
      const dayNumber = date.getUTCDate();
      const isAnchor = !thinLabels || index === 0 || index === earnings.series.length - 1 || dayNumber % 5 === 0;
      return {
        key: point.periodStartUtc,
        label: daily
          ? (isAnchor ? String(dayNumber) : '')
          : date.toLocaleDateString(i18n.language, { month: 'short', timeZone: 'UTC' }),
        value: point.net,
        highlighted: point.periodStartUtc.slice(0, daily ? 10 : 7) === todayKey,
        tooltip: (
          <span>
            {date.toLocaleDateString(i18n.language, daily
              ? { day: 'numeric', month: 'long', timeZone: 'UTC' }
              : { month: 'long', year: 'numeric', timeZone: 'UTC' })}
            {' — '}
            {formatCurrency(point.net, earnings.currencyCode, i18n.language)}
          </span>
        ),
      };
    });
  }, [earnings, i18n.language]);

  // Whatever is on screen, said in words: a shortcut by name, a hand-picked
  // window by its own dates. Never the name of a shortcut over a window that
  // is not that shortcut's.
  const rangeLabel =
    range === 'month'
      ? translate(t, TRANSLATIONS.HOST_EARNINGS_RANGE_MONTH)
      : range === 'year'
        ? translate(t, TRANSLATIONS.HOST_EARNINGS_RANGE_YEAR)
        : range === 'trailing12'
          ? translate(t, TRANSLATIONS.HOST_EARNINGS_RANGE_TRAILING_12)
          : `${activeWindow[0].format('DD/MM/YYYY')} – ${activeWindow[1].format('DD/MM/YYYY')}`;

  // Two controls over one window. The pills fill the date fields in; touching
  // a date field drops the pills (no `value` matches 'custom', so none of
  // them lights up), which is the honest state: the window is no longer any
  // of them.
  const rangeControls = (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
      <SegmentedPillToggle
        value={range}
        onChange={setRange}
        style={{ marginBottom: 0, flex: '1 1 280px' }}
        options={[
          { value: 'month', label: translate(t, TRANSLATIONS.HOST_EARNINGS_RANGE_MONTH) },
          { value: 'year', label: translate(t, TRANSLATIONS.HOST_EARNINGS_RANGE_YEAR) },
          { value: 'trailing12', label: translate(t, TRANSLATIONS.HOST_EARNINGS_RANGE_TRAILING_12) },
        ]}
      />
      <DateRangeFields
        value={activeWindow}
        // Two years back to one year ahead: wide enough for any real
        // question, and narrow enough that the widest window the fields can
        // produce is still inside what the server accepts.
        earliest={EARLIEST_PICKABLE()}
        latest={LATEST_PICKABLE()}
        style={{ flex: isMobile ? '1 1 100%' : '0 1 340px' }}
        fromPlaceholder={translate(t, TRANSLATIONS.HOST_EARNINGS_RANGE_FROM)}
        toPlaceholder={translate(t, TRANSLATIONS.HOST_EARNINGS_RANGE_TO)}
        onChange={(next) => {
          setCustomWindow(next);
          setRange('custom');
        }}
      />
    </div>
  );

  // Both breakdowns are shares of the SAME total, so they are built the same
  // way and read the same way -- see EarningsBreakdownCard.
  const shareOf = useCallback(
    (amount: number) => (earnings && earnings.netInRange > 0 ? Math.round((amount / earnings.netInRange) * 100) : 0),
    [earnings]
  );

  const machineryRows: IEarningsBreakdownRow[] = useMemo(
    () =>
      (earnings?.byMachinery ?? []).map((row) => ({
        key: String(row.machineryId ?? 'other'),
        icon: <IconEngine size={18} />,
        // No title means this row is not one machine: it is everything this
        // host earned away from their own fleet (see the server's own note).
        label: row.machineryTitle ?? translate(t, TRANSLATIONS.HOST_EARNINGS_BY_MACHINE_OTHER),
        amount: money(row.net),
        caption: t(TRANSLATIONS.HOST_EARNINGS_BOOKINGS_COUNT, { count: row.bookingCount }),
        share: shareOf(row.net),
      })),
    [earnings?.byMachinery, money, shareOf, t]
  );

  const serviceRows: IEarningsBreakdownRow[] = useMemo(
    () =>
      (earnings?.byService ?? []).map((row) => ({
        key: String(row.serviceId),
        icon: <IconBriefcase size={18} />,
        label: row.serviceTitle,
        amount: money(row.net),
        caption: t(TRANSLATIONS.HOST_EARNINGS_BOOKINGS_COUNT, { count: row.bookingCount }),
        share: shareOf(row.net),
      })),
    [earnings?.byService, money, shareOf, t]
  );

  const operatorRows: IEarningsBreakdownRow[] = useMemo(
    () =>
      (earnings?.byOperator ?? []).map((row) => ({
        key: String(row.operatorId),
        icon: <IconUsers size={18} />,
        label: row.operatorName,
        amount: money(row.net),
        caption: t(TRANSLATIONS.HOST_EARNINGS_BOOKINGS_COUNT, { count: row.bookingCount }),
        share: shareOf(row.net),
      })),
    [earnings?.byOperator, money, shareOf, t]
  );

  // Shares here are of the pricing-mode total, NOT of the range: the question
  // is how service income splits between the two ways of billing it, and
  // measuring that against a range that also contains rentals would answer a
  // question nobody asked.
  const pricingModeTotal = useMemo(
    () => (earnings?.byPricingMode ?? []).reduce((sum, row) => sum + row.net, 0),
    [earnings?.byPricingMode]
  );

  const pricingModeRows: IEarningsBreakdownRow[] = useMemo(
    () =>
      (earnings?.byPricingMode ?? []).map((row) => {
        const Icon = PRICING_MODE_ICON[row.pricingMode];
        return {
          key: row.pricingMode,
          icon: <Icon size={18} />,
          label: translate(t, PRICING_MODE_LABEL_KEY[row.pricingMode]),
          amount: money(row.net),
          caption: t(TRANSLATIONS.HOST_EARNINGS_BOOKINGS_COUNT, { count: row.bookingCount }),
          share: pricingModeTotal > 0 ? Math.round((row.net / pricingModeTotal) * 100) : 0,
        };
      }),
    [earnings?.byPricingMode, money, pricingModeTotal, t]
  );

  // Service money the split cannot account for, because it was booked before
  // the server started recording which way it was billed. Reported rather
  // than absorbed into whichever side looks likelier -- the whole point of
  // snapshotting the mode was to stop guessing at it.
  const pricingModeShortfall = useMemo(() => {
    const serviceTotal = (earnings?.byService ?? []).reduce((sum, row) => sum + row.net, 0);
    return Math.max(0, serviceTotal - pricingModeTotal);
  }, [earnings?.byService, pricingModeTotal]);

  const conceptRows: IEarningsBreakdownRow[] = useMemo(() => {
    const byCode = new Map((earnings?.byConcept ?? []).map((row) => [row.concept, row]));
    // Walked in the app's own order rather than in whatever order the server
    // happened to sum them, so the list does not reshuffle between periods.
    return CONCEPT_ORDER.flatMap((concept) => {
      const row = byCode.get(concept);
      if (!row) return [];
      const Icon = CONCEPT_ICON[concept];
      return [{
        key: concept,
        icon: <Icon size={18} />,
        label: translate(t, CONCEPT_LABEL_KEY[concept]),
        amount: money(row.net),
        share: shareOf(row.net),
        // Children's bars are shares of their OWN concept, not of the range:
        // inside "accesorios", the useful comparison is between accessories.
        // The server sends them biggest first and already netted, so this only
        // formats them.
        children: row.children.map((child) => ({
          key: child.key,
          icon: null,
          label: child.label,
          amount: money(child.net),
          caption: t(TRANSLATIONS.HOST_EARNINGS_BOOKINGS_COUNT, { count: child.bookingCount }),
          share: row.net > 0 ? Math.round((child.net / row.net) * 100) : 0,
        })),
      }];
    });
  }, [earnings?.byConcept, money, shareOf, t]);

  const renderBody = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      );
    }

    if (loadError || !earnings) {
      return (
        <EmptyState
          icon={<IconCoins size={24} />}
          title={translate(t, TRANSLATIONS.HOST_EARNINGS_LOAD_ERROR)}
          description={translate(t, TRANSLATIONS.HOST_EARNINGS_LOAD_ERROR_DESCRIPTION)}
          action={<Button onClick={load}>{translate(t, TRANSLATIONS.HOST_EARNINGS_RETRY_BUTTON)}</Button>}
        />
      );
    }

    // A host with nothing charged yet and nothing on the way has no trend to
    // read and no money to locate -- one honest empty state beats four
    // panels of zeros.
    const hasAnything =
      earnings.netThisYear !== 0 ||
      // Not `series.length`: the series is zero-filled across the whole range
      // now, so it is never empty -- only its values say whether anything
      // happened.
      earnings.series.some((point) => point.net !== 0) ||
      Object.values(earnings.where).some((value) => value !== 0);

    if (!hasAnything) {
      return (
        <EmptyState
          icon={<IconCoins size={24} />}
          title={translate(t, TRANSLATIONS.HOST_EARNINGS_EMPTY)}
          description={translate(t, TRANSLATIONS.HOST_EARNINGS_EMPTY_DESCRIPTION)}
        />
      );
    }

    return (
      <>
        {/* Nothing is converted between currencies, so say so rather than
            show a confident total that silently adds euros to pounds. */}
        {earnings.mixedCurrencies && (
          <Alert
            type="warning"
            showIcon
            style={{ borderRadius: 14, marginBottom: 16 }}
            message={translate(t, TRANSLATIONS.HOST_EARNINGS_MIXED_CURRENCIES)}
          />
        )}

        {/* Only for a host who actually has both kinds of income -- a
            control that offers to hide half of a business that only has one
            half is a question with one real answer. It is computed over the
            host's whole history, never over the range or the current scope,
            so it cannot disappear the moment it is used. */}
        {earnings.hasMachineryIncome && earnings.hasServiceIncome && (
          <div style={{ marginBottom: 16 }}>
            <SegmentedPillToggle
              value={scope}
              onChange={setScope}
              style={{ marginBottom: 6 }}
              options={[
                { value: 'all', label: translate(t, TRANSLATIONS.HOST_EARNINGS_SCOPE_ALL) },
                { value: 'machinery', label: translate(t, TRANSLATIONS.HOST_EARNINGS_SCOPE_MACHINERY) },
                { value: 'services', label: translate(t, TRANSLATIONS.HOST_EARNINGS_SCOPE_SERVICES) },
              ]}
            />
            {/* Said out loud, because the control sits above figures it does
                NOT touch. Without this a host who picks "Maquinaria" and sees
                "Este mes" stay put would reasonably think it is broken. */}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translate(t, TRANSLATIONS.HOST_EARNINGS_SCOPE_HINT)}
            </Text>
          </div>
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <StatTile
              label={translate(t, TRANSLATIONS.HOST_EARNINGS_TODAY)}
              value={money(earnings.netToday)}
              icon={<IconCalendarStats size={16} />}
            />
          </Col>
          <Col xs={24} sm={8}>
            <StatTile
              emphasis
              label={translate(t, TRANSLATIONS.HOST_EARNINGS_THIS_MONTH)}
              value={money(earnings.netThisMonth)}
              hint={translate(t, TRANSLATIONS.HOST_EARNINGS_NET_HINT)}
              icon={<IconCoins size={16} />}
            />
          </Col>
          <Col xs={24} sm={8}>
            <StatTile
              label={translate(t, TRANSLATIONS.HOST_EARNINGS_THIS_YEAR)}
              value={money(earnings.netThisYear)}
              icon={<IconChartBar size={16} />}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={14}>
            <div
              style={{
                background: token.colorBgContainer,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: 16,
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Title level={5} style={{ margin: 0 }}>
                  {translate(t, TRANSLATIONS.HOST_EARNINGS_TREND_TITLE)}
                </Title>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {rangeLabel}
                  </Text>
                  {/* Bars read a single period off the axis better; a line
                      reads the shape between them better. Which of the two a
                      host wants depends on the question they came with, so
                      it is theirs to pick. */}
                  <Segmented
                    size="small"
                    value={chartVariant}
                    onChange={(value) => chooseChartVariant(value as MiniTrendVariant)}
                    options={[
                      { label: translate(t, TRANSLATIONS.HOST_EARNINGS_CHART_BARS), value: 'bars' },
                      { label: translate(t, TRANSLATIONS.HOST_EARNINGS_CHART_LINE), value: 'line' },
                    ]}
                  />
                </div>
              </div>
              <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 14 }}>
                {translate(t, TRANSLATIONS.HOST_EARNINGS_TREND_SUBTITLE)}
              </Text>

              <MiniTrendChart
                points={chartPoints}
                variant={chartVariant}
                height={isMobile ? 130 : 170}
                emptyLabel={translate(t, TRANSLATIONS.HOST_EARNINGS_TREND_EMPTY)}
              />

              {/* The three figures behind the bars, so the commission is a
                  fact on screen rather than a subtraction the host has to do
                  against their own invoices. */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                  gap: 12,
                  marginTop: 16,
                  paddingTop: 16,
                  borderTop: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                {[
                  { labelKey: TRANSLATIONS.HOST_EARNINGS_RANGE_GROSS, value: money(earnings.grossInRange) },
                  { labelKey: TRANSLATIONS.HOST_EARNINGS_RANGE_FEE, value: `-${money(earnings.platformFeeInRange)}` },
                  { labelKey: TRANSLATIONS.HOST_EARNINGS_RANGE_NET, value: money(earnings.netInRange) },
                  { labelKey: TRANSLATIONS.HOST_EARNINGS_RANGE_BOOKINGS, value: String(earnings.bookingsInRange) },
                ].map((figure) => (
                  <div key={figure.labelKey}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                      {translate(t, figure.labelKey)}
                    </Text>
                    <Text strong style={{ fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
                      {figure.value}
                    </Text>
                  </div>
                ))}
              </div>

              {/* Only when there is one. It is already absorbed by the net
                  figures above (the lines themselves shrank when it was
                  issued), so it is reported, never subtracted again. */}
              {earnings.refundedInRange > 0 && (
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 12 }}>
                  {t(TRANSLATIONS.HOST_EARNINGS_RANGE_REFUNDED, { amount: money(earnings.refundedInRange) })}
                </Text>
              )}
            </div>
          </Col>

          <Col xs={24} lg={10}>
            <HostEarningsWhereCard where={earnings.where} currencyCode={earnings.currencyCode} />
          </Col>
        </Row>

        {/* "Por máquina" is only a breakdown when there is more than one
            thing in it. A provider who owns no machines gets a single row
            reading "Servicios prestados — 100%", which is a bar of the whole
            card saying nothing; their answer is "Por servicio" below. */}
        {(() => {
          const showMachinery =
            scope !== 'services' && machineryRows.length > 0 && earnings.byMachinery.some((row) => row.machineryId !== null);
          const cards = [
            showMachinery,
            conceptRows.length > 0,
            serviceRows.length > 0,
            operatorRows.length > 0,
            pricingModeRows.length > 0,
          ];
          if (!cards.some(Boolean)) return null;

          return (
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              {showMachinery && (
                <Col xs={24} lg={12}>
                  <EarningsBreakdownCard
                    title={translate(t, TRANSLATIONS.HOST_EARNINGS_BY_MACHINE_TITLE)}
                    subtitle={translate(t, TRANSLATIONS.HOST_EARNINGS_BY_MACHINE_SUBTITLE)}
                    rows={machineryRows}
                  />
                </Col>
              )}
              {conceptRows.length > 0 && (
                <Col xs={24} lg={12}>
                  <EarningsBreakdownCard
                    title={translate(t, TRANSLATIONS.HOST_EARNINGS_BY_CONCEPT_TITLE)}
                    subtitle={translate(t, TRANSLATIONS.HOST_EARNINGS_BY_CONCEPT_SUBTITLE)}
                    rows={conceptRows}
                  />
                </Col>
              )}
              {serviceRows.length > 0 && (
                <Col xs={24} lg={12}>
                  <EarningsBreakdownCard
                    title={translate(t, TRANSLATIONS.HOST_EARNINGS_BY_SERVICE_TITLE)}
                    subtitle={translate(t, TRANSLATIONS.HOST_EARNINGS_BY_SERVICE_SUBTITLE)}
                    rows={serviceRows}
                  />
                </Col>
              )}
              {operatorRows.length > 0 && (
                <Col xs={24} lg={12}>
                  <EarningsBreakdownCard
                    title={translate(t, TRANSLATIONS.HOST_EARNINGS_BY_OPERATOR_TITLE)}
                    subtitle={translate(t, TRANSLATIONS.HOST_EARNINGS_BY_OPERATOR_SUBTITLE)}
                    rows={operatorRows}
                  />
                </Col>
              )}
              {pricingModeRows.length > 0 && (
                <Col xs={24} lg={12}>
                  <EarningsBreakdownCard
                    title={translate(t, TRANSLATIONS.HOST_EARNINGS_BY_PRICING_MODE_TITLE)}
                    subtitle={translate(t, TRANSLATIONS.HOST_EARNINGS_BY_PRICING_MODE_SUBTITLE)}
                    rows={pricingModeRows}
                  />
                  {/* Only when the split genuinely does not cover all of the
                      service income -- see pricingModeShortfall. */}
                  {pricingModeShortfall > 0 && (
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                      {t(TRANSLATIONS.HOST_EARNINGS_PRICING_MODE_PARTIAL, { amount: money(pricingModeShortfall) })}
                    </Text>
                  )}
                </Col>
              )}
            </Row>
          );
        })()}

        {/* Said once, at the bottom, where a footnote belongs: these are the
            server's own rules (see HostEarningsCalculator) and a host reading
            a figure they disagree with deserves to know how it was counted. */}
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 16 }}>
          {translate(t, TRANSLATIONS.HOST_EARNINGS_FOOTNOTE)}
        </Text>
      </>
    );
  };

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 1100 }}>
        {/* The controls sit under the subtitle on both sizes rather than in
            the header's action slot: two of them side by side is more than
            that slot is for, and splitting them by breakpoint would be a
            second layout to keep in step for no gain. */}
        <PageHeaderRow subtitle={translate(t, TRANSLATIONS.HOST_EARNINGS_PAGE_SUBTITLE)} />
        {rangeControls}
        {renderBody()}
      </div>
    </AppLayout>
  );
};

export default HostEarnings;
